import { useState } from 'react';
import { X, Check, Trash2, Plus, Building2 } from 'lucide-react';
import { useConnectionStore, ZoomConnection } from '@/stores/connectionStore';

interface AccountManagerProps {
  onClose: () => void;
  onAddAccount: () => void;
}

export function AccountManager({ onClose, onAddAccount }: AccountManagerProps) {
  const { connections, activeAccountId, isLoading, disconnect, setActiveAccount } = useConnectionStore();
  const [confirmDisconnect, setConfirmDisconnect] = useState<string | null>(null);

  const handleSetActive = (accountId: string) => {
    setActiveAccount(accountId);
  };

  const handleDisconnect = async (accountId: string) => {
    await disconnect(accountId);
    setConfirmDisconnect(null);
  };

  const handleAddAccount = () => {
    onAddAccount();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-lg bg-card shadow-xl border border-border">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-foreground">
            Manage Zoom Connections
          </h2>
          <button
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {connections.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No Zoom accounts connected</p>
              <p className="text-sm mt-1">Add an account to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {connections.map((conn) => (
                <AccountCard
                  key={conn.id}
                  connection={conn}
                  isActive={conn.accountId === activeAccountId}
                  isConfirmingDisconnect={confirmDisconnect === conn.accountId}
                  isLoading={isLoading}
                  onSetActive={() => handleSetActive(conn.accountId)}
                  onDisconnect={() => handleDisconnect(conn.accountId)}
                  onConfirmDisconnect={() => setConfirmDisconnect(conn.accountId)}
                  onCancelDisconnect={() => setConfirmDisconnect(null)}
                />
              ))}
            </div>
          )}

          {/* Add Account Button */}
          <button
            onClick={handleAddAccount}
            className="w-full flex items-center justify-center gap-2 rounded-md border border-border px-4 py-3 text-foreground hover:bg-secondary transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Account
          </button>
        </div>

        {/* Footer */}
        <div className="border-t border-border px-6 py-4">
          <button
            onClick={onClose}
            className="w-full rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

interface AccountCardProps {
  connection: ZoomConnection;
  isActive: boolean;
  isConfirmingDisconnect: boolean;
  isLoading: boolean;
  onSetActive: () => void;
  onDisconnect: () => void;
  onConfirmDisconnect: () => void;
  onCancelDisconnect: () => void;
}

function AccountCard({
  connection,
  isActive,
  isConfirmingDisconnect,
  isLoading,
  onSetActive,
  onDisconnect,
  onConfirmDisconnect,
  onCancelDisconnect,
}: AccountCardProps) {
  return (
    <div
      className={`rounded-lg border p-4 ${
        isActive
          ? 'border-green-700 bg-green-900/10'
          : 'border-border bg-secondary/50'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-foreground truncate">
              {connection.accountName}
            </span>
            {isActive && (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-900/30 px-2 py-0.5 text-xs text-green-400">
                <Check className="h-3 w-3" />
                Active
              </span>
            )}
          </div>
          <div className="mt-1 text-sm text-muted-foreground">
            Account ID: {connection.accountId}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            Connected: {new Date(connection.connectedAt).toLocaleDateString()}
            {connection.lastUsedAt && (
              <> · Last used: {new Date(connection.lastUsedAt).toLocaleDateString()}</>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-3 flex items-center gap-2">
        {!isActive && (
          <button
            onClick={onSetActive}
            disabled={isLoading}
            className="flex-1 rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:bg-secondary disabled:opacity-50"
          >
            Set as Active
          </button>
        )}

        {isConfirmingDisconnect ? (
          <div className="flex items-center gap-2 flex-1">
            <button
              onClick={onDisconnect}
              disabled={isLoading}
              className="flex-1 rounded-md bg-red-600 px-3 py-1.5 text-sm text-white hover:bg-red-700 disabled:opacity-50"
            >
              Confirm
            </button>
            <button
              onClick={onCancelDisconnect}
              disabled={isLoading}
              className="flex-1 rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:bg-secondary disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={onConfirmDisconnect}
            disabled={isLoading}
            className="rounded-md border border-red-700/50 px-3 py-1.5 text-sm text-red-400 hover:bg-red-900/20 disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
